import ShareIcon from '@mui/icons-material/Share';
import type { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import type { ComponentProps, MouseEvent } from 'react';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { SilobagShareModal } from '@old/features/silobags/silobag-share/silobag-share';
import {
  listSilobagPermissionsService,
  type SilobagPermissionListQuery,
} from '@siloreal:shared:api/service/silobag/silobag-permission-list';
import type { SilobagRow } from '@siloreal:modules:private/silobag-list/config/extra/types';
import * as UI from '@siloreal:shared:ui';
import { useSilobagListContext } from '../../../context';
import * as HOOKS from '@siloreal:shared:hooks';

type ShareActionProps = {
  actionId: string;
  disabled?: boolean;
};

type ShareModalProps = ComponentProps<typeof SilobagShareModal>;
type SharePermissionSummary = NonNullable<ShareModalProps['data']>;
type ShareSilobagItem = ShareModalProps['silobagsData'][number];
type SharePermissionQueryPayload = { query: SilobagPermissionListQuery };

type ShareModalState = {
  ids: string[];
  data?: SharePermissionSummary;
  massiveData: SharePermissionSummary[];
  silobagsData: ShareSilobagItem[];
  siloName?: string;
  countrysideName?: string;
} | null;

type SilobagSharedState = {
  message: string;
  state: boolean;
  type: 'success' | 'error';
};

async function fetchSharePermissions(ids: string[]): Promise<SharePermissionSummary[]> {
  const permissions = await Promise.all(ids.map((id) => listSilobagPermissionsService({ query: id })));

  return permissions as SharePermissionSummary[];
}

function getSelectedIds(selectionModel: GridRowSelectionModel): string[] {
  return Array.from(selectionModel.ids).map(String);
}

function useSilobagShareModal() {
  const {
    async: {
      list: { current },
    },
  } = useSilobagListContext();
  const [shareModal, setShareModal] = useState<ShareModalState>(null);
  const [isPermissionLoading, setIsPermissionLoading] = useState(false);

  const buildSelectedSilobagsData = (
    ids: string[],
    rows: SilobagRow[] = []
  ): NonNullable<ShareModalState> => {
    const currentRowById = new Map((current?.data ?? []).map((row) => [String(row.id), row]));
    const selectedRowById = new Map(rows.map((row) => [String(row.id), row]));
    const getRow = (id: string) => selectedRowById.get(id) ?? currentRowById.get(id);

    const selectedRows = ids.map((id) => {
      const row = getRow(id);

      return {
        id,
        name: row?.name ?? id,
        full_permission: row?.full_permission ?? false,
      };
    });

    const firstSelectedRow = getRow(ids[0]);

    return {
      ids,
      massiveData: [],
      silobagsData: selectedRows,
      siloName: ids.length === 1 ? firstSelectedRow?.name : undefined,
      countrysideName: ids.length === 1 ? firstSelectedRow?.lots?.countrysides?.name : undefined,
    };
  };

  const handleGetData = async ({ query }: SharePermissionQueryPayload): Promise<void> => {
    const ids = (Array.isArray(query) ? query : [query]).map(String);

    if (ids.length === 0) return;

    setIsPermissionLoading(true);

    const i18n = HOOKS.useLocale.getState();

    try {
      const permissions = await fetchSharePermissions(ids);

      setShareModal((currentModal) => {
        if (!currentModal) return currentModal;

        return {
          ...currentModal,
          data: permissions[0],
          massiveData: permissions,
        };
      });
    } catch {
      toast.error(i18n.t('temp.common.toasts.error.update_silobag_permissions'));
    } finally {
      setIsPermissionLoading(false);
    }
  };

  const openShareModal = async (ids: string[], selectedRows?: SilobagRow[]): Promise<void> => {
    if (ids.length === 0) return;

    setIsPermissionLoading(true);

    const i18n = HOOKS.useLocale.getState();

    try {
      const permissions = await fetchSharePermissions(ids);

      setShareModal({
        ...buildSelectedSilobagsData(ids, selectedRows),
        data: permissions[0],
        massiveData: permissions,
      });
    } catch {
      toast.error(i18n.t('temp.common.toasts.error.obtain_silobag_permissions'));
    } finally {
      setIsPermissionLoading(false);
    }
  };

  const handleSetModalOpen = (open: boolean): void => {
    if (!open) {
      setShareModal(null);
    }
  };

  const handleSilobagShared = (state: SilobagSharedState): void => {
    if (!state.state) return;

    if (state.type === 'success') {
      toast.success(state.message);
      return;
    }

    toast.error(state.message);
  };

  const modal = shareModal ? (
    <SilobagShareModal
      open
      setOpen={handleSetModalOpen}
      siloId={shareModal.ids}
      siloName={shareModal.siloName}
      data={shareModal.data}
      massiveData={shareModal.massiveData}
      isLoadingData={isPermissionLoading}
      getData={handleGetData}
      setSilobagShared={handleSilobagShared}
      CountrysideName={shareModal.countrysideName}
      silobagsData={shareModal.silobagsData}
    />
  ) : null;

  return {
    isPermissionLoading,
    modal,
    openShareModal,
  };
}

export function ShareAction(props: ShareActionProps) {
  const { actionId, disabled } = props;
  const explorer = UI.collection.useExplorer();
  const { isPermissionLoading, modal, openShareModal } = useSilobagShareModal();
  const handleSelectionConfirm = async (selectionModel: GridRowSelectionModel): Promise<void> => {
    await openShareModal(getSelectedIds(selectionModel));
  };

  const handleClick = (): void => {
    explorer.startSelection({
      id: actionId,
      onConfirm: handleSelectionConfirm,
    });
  };

  const i18n = HOOKS.useLocale();

  return (
    <>
      <UI.Button
        variant="minor"
        uppercase={false}
        icon={{ value: <ShareIcon fontSize="small" />, position: 'end' }}
        onClick={handleClick}
        loading={isPermissionLoading}
        disabled={isPermissionLoading || disabled}
        tid={`${actionId}-btn`}
      >
        {i18n.t('temp.common.share')}
      </UI.Button>

      {modal}
    </>
  );
}

const SHARED_ICON_GLOW =
  'drop-shadow(0 0 3px rgba(115, 189, 0, 0.88)) drop-shadow(0 0 4px rgba(115, 189, 0, 0.48))';

type ShareRowActionProps = {
  row: SilobagRow;
};

export function ShareRowAction(props: ShareRowActionProps) {
  const { row } = props;
  const { isPermissionLoading, modal, openShareModal } = useSilobagShareModal();
  const isSharedExternally = row.is_shared_externally ?? false;

  const handleClick = (event: MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation();
    void openShareModal([String(row.id)], [row]);
  };

  return (
    <>
      <UI.Button
        variant={isSharedExternally ? 'primary-hollow' : 'alternative-hollow'}
        removeBorder
        icon={{
          value: (
            <ShareIcon
              fontSize="small"
              sx={{
                color: isSharedExternally ? '#73bd00' : undefined,
                filter: isSharedExternally ? SHARED_ICON_GLOW : undefined,
              }}
            />
          ),
          only: true,
        }}
        ariaLabel={isSharedExternally ? 'Silobolsa compartida' : 'Compartir silobolsa'}
        onClick={handleClick}
        loading={isPermissionLoading}
        disabled={isPermissionLoading}
        tid="silobag-list-row-share-btn"
      />

      {modal}
    </>
  );
}
